import * as THREE from 'three';
import { planetPositions } from './planets';
import { CONSTELLATIONS, STARS } from './sky-catalog';

// The night sky the Santiago landing opens on, as it really stands over the city at 23:00
// on the first night of the hack: every naked-eye star of the Hipparcos catalogue and the
// planets where they are at that moment — nothing invented. The IAU figures are there
// too, but unseen: one only surfaces, faintly and with its name, while the pointer rests
// on one of its stars.
//
// Flat on purpose: the page's own palette only — its near-black background and the
// ph26 ink — so the sky reads as part of the landing, not as a photo behind it.
//
// Directions are az/alt in degrees with az 0 straight away from the landing's resting
// camera (world −z) and positive az to the right — the same frame the telescope slews in.
// That camera faces compass bearing HEADING.

export const SKY_RADIUS = 2400;
export const BACKGROUND = '#1a1a1a'; // theme-ph26 --background (0 0% 10%)

// Santiago, 20 Nov 2026 at 23:00 (UTC−3).
export const SITE = { lat: -33.45, lon: -70.66 };
export const NIGHT = Date.UTC(2026, 10, 21, 2, 0, 0);
// East-southeast: Orion rising upside down, Sirius under it, Canopus to the right and the
// Milky Way running up between them.
export const HEADING = 102;

const DEG = Math.PI / 180;

export function dirFromAzAlt(azDeg: number, altDeg: number, target = new THREE.Vector3()) {
  const az = THREE.MathUtils.degToRad(azDeg);
  const alt = THREE.MathUtils.degToRad(altDeg);
  return target.set(Math.sin(az) * Math.cos(alt), Math.sin(alt), -Math.cos(az) * Math.cos(alt));
}

export function azAltFromDir(dir: THREE.Vector3) {
  return {
    az: THREE.MathUtils.radToDeg(Math.atan2(dir.x, -dir.z)),
    alt: THREE.MathUtils.radToDeg(Math.asin(THREE.MathUtils.clamp(dir.y, -1, 1))),
  };
}

// Local sidereal time in degrees (GMST, good to well under a degree for a sky like this).
function siderealTime(ms: number, lon: number) {
  const days = ms / 86_400_000 + 2440587.5 - 2451545;
  return (((280.46061837 + 360.98564736629 * days + lon) % 360) + 360) % 360;
}
const LST = siderealTime(NIGHT, SITE.lon);
const SIN_LAT = Math.sin(SITE.lat * DEG);
const COS_LAT = Math.cos(SITE.lat * DEG);

// Where a star at (ra, dec) stands in the scene, that night, from Santiago.
export function dirFromRaDec(ra: number, dec: number, target = new THREE.Vector3()) {
  const h = (LST - ra) * DEG;
  const d = dec * DEG;
  const alt = Math.asin(Math.sin(d) * SIN_LAT + Math.cos(d) * COS_LAT * Math.cos(h));
  const bearing = Math.atan2(-Math.sin(h) * Math.cos(d), Math.sin(d) * COS_LAT - Math.cos(d) * SIN_LAT * Math.cos(h));
  return dirFromAzAlt(bearing / DEG - HEADING, alt / DEG, target);
}

export function mulberry32(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// A named object the telescope knows about: centred in the field, its read-out appears.
export type SkyTarget = {
  id: string;
  name: string; // the constellation
  object: string;
  label: string; // object class
  ra: number;
  dec: number;
  distance: string;
  fact: string;
};

// In the order ‹ › steps through them, at their real places in the sky (roughly from the
// left of the resting view to the right; Crux and Alpha Centauri are skimming the southern
// horizon at that hour).
export const SKY_TARGETS: SkyTarget[] = [
  {
    id: 'orion',
    name: 'Orión',
    object: 'Nebulosa de Orión',
    label: 'nebulosa de emisión · M42',
    ra: 83.82,
    dec: -5.39,
    distance: '1.344 años luz',
    fact: 'Desde Chile, Orión se ve de cabeza. Sobre su cinturón brilla M42, la fábrica de estrellas grandes más cercana a la Tierra.',
  },
  {
    id: 'sirius',
    name: 'Perro Mayor',
    object: 'Sirio',
    label: 'estrella doble · α Canis Majoris',
    ra: 101.29,
    dec: -16.72,
    distance: '8,6 años luz',
    fact: 'La estrella más brillante del cielo nocturno. La acompaña Sirio B, una enana blanca del tamaño de la Tierra con la masa del Sol.',
  },
  {
    id: 'carina',
    name: 'Quilla',
    object: 'Nebulosa de Carina',
    label: 'nebulosa de emisión',
    ra: 161.26,
    dec: -59.87,
    distance: '7.500 años luz',
    fact: 'Cuatro veces más grande que la de Orión y solo visible desde el hemisferio sur. Adentro está Eta Carinae, una estrella que podría estallar.',
  },
  {
    id: 'dorado',
    name: 'Dorado',
    object: 'Gran Nube de Magallanes',
    label: 'galaxia satélite',
    ra: 80.89,
    dec: -69.76,
    distance: '160.000 años luz',
    fact: 'Una galaxia vecina que orbita la Vía Láctea, visible a simple vista desde el desierto. El ELT podrá separar sus estrellas una por una.',
  },
  {
    id: 'tucana',
    name: 'Tucán',
    object: '47 Tucanae',
    label: 'cúmulo globular',
    ra: 6.02,
    dec: -72.08,
    distance: '13.000 años luz',
    fact: 'El segundo cúmulo globular más brillante del cielo: cientos de miles de estrellas de más de 10.000 millones de años, junto a la Nube Chica de Magallanes.',
  },
  {
    id: 'crux',
    name: 'Cruz del Sur',
    object: 'Acrux',
    label: 'estrella múltiple · α Crucis',
    ra: 186.65,
    dec: -63.1,
    distance: '320 años luz',
    fact: 'La constelación más chica del cielo y la guía de los navegantes del sur. Acrux, su pie, son en realidad varias estrellas azules.',
  },
  {
    id: 'centaurus',
    name: 'Centauro',
    object: 'Próxima b',
    label: 'exoplaneta rocoso · Alfa Centauri',
    ra: 217.43,
    dec: -62.68,
    distance: '4,24 años luz',
    fact: 'Alfa Centauri es el sistema estelar más cercano al Sol. Su compañera Próxima tiene un planeta en la zona habitable que el ELT busca fotografiar.',
  },
];

const STAR_VERTEX = `
  attribute float aSize;
  attribute vec3 aColor;
  attribute float aDelay;
  attribute float aPhase;
  attribute float aKind; // 0 star, 1 planet, 2 planet only the telescope shows
  uniform float uTime;
  uniform float uPixelRatio;
  uniform float uFade;
  uniform float uScope; // 0 naked eye → 1 through the telescope
  uniform float uPlanetFade; // the planets' points give way to their discs up close
  varying vec3 vColor;
  varying float vAlpha;
  varying float vBright;
  void main() {
    vBright = smoothstep(3.5, 11.0, aSize);
    float on = smoothstep(aDelay, aDelay + 0.5, uTime);
    float flash = on * (1.0 - on) * 4.0;
    // stars twinkle, planets hold steady, and the telescope steadies everything
    float twinkle = 0.72 + 0.28 * sin(uTime * (1.3 + aPhase * 3.1) + aPhase * 41.0);
    twinkle = mix(twinkle, 1.0, max(step(0.5, aKind), uScope * 0.8));
    float shown = (aKind > 1.5 ? uScope : 1.0) * (aKind > 0.5 ? uPlanetFade : 1.0);
    vColor = aColor;
    vAlpha = on * twinkle * uFade * shown;
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    gl_Position = projectionMatrix * mv;
    gl_PointSize = aSize * uPixelRatio * (1.0 + flash * 1.8) * (1.0 + uScope * 0.6);
  }`;

const STAR_FRAGMENT = `
  varying vec3 vColor;
  varying float vAlpha;
  varying float vBright;
  void main() {
    float d = length(gl_PointCoord - 0.5);
    float core = smoothstep(0.5, 0.0, d);
    // the bright ones get a fuller core and a soft halo, so they read as the beacons they are
    float a = (pow(core, mix(2.2, 1.3, vBright)) + exp(-d * d * 22.0) * 0.45 * vBright) * vAlpha;
    if (a < 0.003) discard;
    gl_FragColor = vec4(vColor * a, a);
  }`;

export type Sky = {
  group: THREE.Group;
  uniforms: { uTime: { value: number }; uPixelRatio: { value: number }; uFade: { value: number }; uScope: { value: number }; uPlanetFade: { value: number } };
  dispose: () => void;
};

// The planets that night, with where they stand in the scene; those under the horizon too.
export const PLANET_SKY = planetPositions(NIGHT).map((p) => ({ ...p, dir: dirFromRaDec(p.ra, p.dec) }));

// Star tints by colour index, in the page's palette: the hot blue-white stars white, the
// cool orange-red ones full ink.
const tint = (bv: number) => (bv < 0.15 ? '#ffffff' : bv < 0.6 ? '#f6ffc2' : bv < 1.2 ? '#efff80' : '#eaff00');

export function createSky(pixelRatio: number): Sky {
  const rand = mulberry32(2611);
  const group = new THREE.Group();
  const uniforms = {
    uTime: { value: 0 },
    uPixelRatio: { value: pixelRatio },
    uFade: { value: 1 },
    uScope: { value: 0 },
    uPlanetFade: { value: 1 },
  };

  const positions: number[] = [];
  const sizes: number[] = [];
  const colors: number[] = [];
  const delays: number[] = [];
  const phases: number[] = [];
  const kinds: number[] = [];
  const v = new THREE.Vector3();
  const c = new THREE.Color();

  const pushStar = (dir: THREE.Vector3, size: number, color: string, brightness: number, delay: number, kind = 0) => {
    if (dir.y < -0.02) return; // below the horizon that night
    v.copy(dir).multiplyScalar(SKY_RADIUS);
    positions.push(v.x, v.y, v.z);
    sizes.push(size);
    c.set(color).multiplyScalar(brightness);
    colors.push(c.r, c.g, c.b);
    delays.push(delay);
    phases.push(rand());
    kinds.push(kind);
  };

  // The catalogue, brightest first; they light up in that order.
  for (let i = 0; i < STARS.length; i += 5) {
    const mag = STARS[i + 2];
    const light = THREE.MathUtils.clamp((6 - mag) / 7, 0, 1); // 0 at the naked-eye limit, 1 at Sirius
    // steep, so the handful of first-magnitude stars carry the figures the eye knows
    const size = 1.1 + light ** 1.6 * 13;
    pushStar(dirFromRaDec(STARS[i], STARS[i + 1], v), size, tint(STARS[i + 3]), 0.45 + light * 0.55, 0.1 + (1 - light) * 2.0 + rand() * 0.5);
  }

  // The planets: steady points, a touch warmer than the stars. The ones too faint for the
  // naked eye (Neptune) only show through the telescope.
  for (const planet of PLANET_SKY) {
    const light = THREE.MathUtils.clamp((6 - planet.mag) / 7, 0, 1);
    pushStar(planet.dir, Math.max(2.6, 1.1 + light ** 1.6 * 13), '#efff80', 0.55 + light * 0.45, 0.3, planet.mag > 6 ? 2 : 1);
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('aSize', new THREE.Float32BufferAttribute(sizes, 1));
  geometry.setAttribute('aColor', new THREE.Float32BufferAttribute(colors, 3));
  geometry.setAttribute('aDelay', new THREE.Float32BufferAttribute(delays, 1));
  geometry.setAttribute('aPhase', new THREE.Float32BufferAttribute(phases, 1));
  geometry.setAttribute('aKind', new THREE.Float32BufferAttribute(kinds, 1));
  const stars = new THREE.Points(
    geometry,
    new THREE.ShaderMaterial({
      uniforms: { uTime: uniforms.uTime, uPixelRatio: uniforms.uPixelRatio, uFade: uniforms.uFade, uScope: uniforms.uScope, uPlanetFade: uniforms.uPlanetFade },
      vertexShader: STAR_VERTEX,
      fragmentShader: STAR_FRAGMENT,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    }),
  );
  stars.frustumCulled = false;
  stars.renderOrder = -5;
  group.add(stars);

  return {
    group,
    uniforms,
    dispose: () => {
      geometry.dispose();
      (stars.material as THREE.Material).dispose();
    },
  };
}

// ── the deep layer ──────────────────────────────────────────────────────────────

// Hipparcos past the naked eye (magnitudes 6–8, sky-deep.bin, see the generator): only
// through the telescope, and only once fetched. Each star fades in as the telescope's
// limiting magnitude (uLimit, raised by the engine with magnification) passes it.
const DEEP_VERTEX = `
  attribute float aMag;
  attribute vec3 aColor;
  uniform float uPixelRatio;
  uniform float uScope;
  uniform float uLimit;
  varying vec3 vColor;
  varying float vAlpha;
  void main() {
    float over = uLimit - aMag;
    vAlpha = smoothstep(-0.3, 0.5, over) * (0.4 + 0.6 * clamp(over / 2.5, 0.0, 1.0)) * uScope;
    vColor = aColor;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    gl_PointSize = (1.1 + clamp(over, 0.0, 3.0) * 0.55) * uPixelRatio * (1.0 + uScope * 0.6);
  }`;

const DEEP_FRAGMENT = `
  varying vec3 vColor;
  varying float vAlpha;
  void main() {
    float d = length(gl_PointCoord - 0.5);
    float a = pow(smoothstep(0.5, 0.0, d), 2.0) * vAlpha;
    if (a < 0.003) discard;
    gl_FragColor = vec4(vColor * a, a);
  }`;

export type DeepSky = {
  group: THREE.Group;
  uniforms: { uScope: { value: number }; uLimit: { value: number } };
  load: () => Promise<void>; // idempotent
  dispose: () => void;
};

export function createDeepSky(pixelRatio: number, url: string): DeepSky {
  const group = new THREE.Group();
  const uniforms = { uScope: { value: 0 }, uLimit: { value: 6 } };
  const material = new THREE.ShaderMaterial({
    uniforms: { uPixelRatio: { value: pixelRatio }, uScope: uniforms.uScope, uLimit: uniforms.uLimit },
    vertexShader: DEEP_VERTEX,
    fragmentShader: DEEP_FRAGMENT,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
  let geometry: THREE.BufferGeometry | null = null;
  let loading: Promise<void> | null = null;

  const build = (bytes: Uint8Array) => {
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const n = bytes.length / 5;
    const positions: number[] = [];
    const mags: number[] = [];
    const colors: number[] = [];
    const v = new THREE.Vector3();
    const c = new THREE.Color();
    for (let i = 0; i < n; i++) {
      const ra = (view.getUint16(i * 4, true) / 65535) * 360;
      const dec = (view.getUint16(i * 4 + 2, true) / 65535) * 180 - 90;
      dirFromRaDec(ra, dec, v);
      if (v.y < -0.02) continue; // under the horizon that night
      const packed = bytes[n * 4 + i];
      mags.push(6 + ((packed >> 4) / 15) * 2);
      v.multiplyScalar(SKY_RADIUS);
      positions.push(v.x, v.y, v.z);
      c.set(tint(((packed & 15) / 15) * 2.1 - 0.3)).multiplyScalar(0.8);
      colors.push(c.r, c.g, c.b);
    }
    geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geometry.setAttribute('aMag', new THREE.Float32BufferAttribute(mags, 1));
    geometry.setAttribute('aColor', new THREE.Float32BufferAttribute(colors, 3));
    const points = new THREE.Points(geometry, material);
    points.frustumCulled = false;
    points.renderOrder = -6;
    group.add(points);
  };

  return {
    group,
    uniforms,
    load: () => {
      loading ??= fetch(url)
        .then((response) => (response.ok ? response.arrayBuffer() : Promise.reject(new Error(String(response.status)))))
        .then((buffer) => build(new Uint8Array(buffer)))
        .catch(() => {
          loading = null; // the naked-eye sky still works; try again next time
        });
      return loading;
    },
    dispose: () => {
      geometry?.dispose();
      material.dispose();
    },
  };
}

// ── constellations ──────────────────────────────────────────────────────────────

// Every IAU figure is drawn all the time, at an opacity of its own that is zero unless the
// pointer rests on it.
const FIGURE_LINE_VERTEX = `
  attribute float aGroup;
  uniform float uAlpha[${CONSTELLATIONS.length}];
  varying float vAlpha;
  void main() {
    vAlpha = uAlpha[int(aGroup + 0.5)];
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }`;

const FIGURE_LINE_FRAGMENT = `
  uniform vec3 uColor;
  uniform float uFade;
  varying float vAlpha;
  void main() {
    if (vAlpha < 0.002) discard;
    gl_FragColor = vec4(uColor * vAlpha * uFade, 1.0);
  }`;

const LINE_ALPHA = 0.26; // a hovered figure, at its brightest: a hint, not a drawing
const PICK_MAG = 4.6; // stars fainter than this don't wake their constellation

export type Constellations = {
  group: THREE.Group;
  uniforms: { uFade: { value: number } };
  names: string[];
  // The constellation whose star is nearest `dir`, within `maxRad`; `current` wins ties.
  pick: (dir: THREE.Vector3, maxRad: number, current: number) => number;
  // Where to hang a figure's name: the middle of its part above the horizon.
  anchor: (index: number) => THREE.Vector3;
  setHover: (index: number) => void;
  update: (dt: number) => void;
  dispose: () => void;
};

export function createConstellations(ink: string): Constellations {
  const count = CONSTELLATIONS.length;
  const alpha = new Float32Array(count);
  let hover = -1;
  const uniforms = { uFade: { value: 1 } };

  const positions: number[] = [];
  const groups: number[] = [];
  const hits: Array<{ dir: THREE.Vector3; group: number }> = [];
  const anchors = CONSTELLATIONS.map(() => new THREE.Vector3());
  const r = SKY_RADIUS * 0.98;

  CONSTELLATIONS.forEach((figure, group) => {
    for (const polyline of figure.lines) {
      const dirs: THREE.Vector3[] = [];
      for (let i = 0; i < polyline.length; i += 2) dirs.push(dirFromRaDec(polyline[i], polyline[i + 1]));
      for (const d of dirs) {
        hits.push({ dir: d, group });
        if (d.y > 0) anchors[group].add(d);
      }
      for (let i = 0; i + 1 < dirs.length; i++) {
        // stop short of each star so the figure reads as joined points, not a wire
        const a = dirs[i];
        const b = dirs[i + 1];
        const gap = Math.min(0.3, THREE.MathUtils.degToRad(0.7) / Math.max(1e-4, a.angleTo(b)));
        const pa = a.clone().lerp(b, gap).normalize().multiplyScalar(r);
        const pb = b.clone().lerp(a, gap).normalize().multiplyScalar(r);
        positions.push(pa.x, pa.y, pa.z, pb.x, pb.y, pb.z);
        groups.push(group, group);
      }
    }
  });
  anchors.forEach((a) => (a.lengthSq() > 0 ? a.normalize() : a.set(0, 1, 0)));
  // the rest of each figure's brighter stars count as touching it too
  for (let i = 0; i < STARS.length; i += 5) {
    if (STARS[i + 2] > PICK_MAG || STARS[i + 4] < 0) continue;
    hits.push({ dir: dirFromRaDec(STARS[i], STARS[i + 1]), group: STARS[i + 4] });
  }
  const visibleHits = hits.filter((h) => h.dir.y > 0);

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('aGroup', new THREE.Float32BufferAttribute(groups, 1));
  const material = new THREE.ShaderMaterial({
    uniforms: { uAlpha: { value: alpha }, uFade: uniforms.uFade, uColor: { value: new THREE.Color(ink) } },
    vertexShader: FIGURE_LINE_VERTEX,
    fragmentShader: FIGURE_LINE_FRAGMENT,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
  const lines = new THREE.LineSegments(geometry, material);
  lines.frustumCulled = false;
  lines.renderOrder = -4;
  const group = new THREE.Group();
  group.add(lines);

  return {
    group,
    uniforms,
    names: CONSTELLATIONS.map((c) => c.name),
    pick: (dir, maxRad, current) => {
      let best = -1;
      let bestAngle = maxRad;
      for (const hit of visibleHits) {
        const angle = hit.dir.angleTo(dir) * (hit.group === current ? 0.8 : 1);
        if (angle < bestAngle) {
          bestAngle = angle;
          best = hit.group;
        }
      }
      return best;
    },
    anchor: (index) => anchors[index],
    setHover: (index) => {
      hover = index;
    },
    update: (dt) => {
      // the old figure fades out faster than the new one comes in
      for (let i = 0; i < count; i++) {
        const goal = i === hover ? LINE_ALPHA : 0;
        const rate = goal > alpha[i] ? 2.2 : 4;
        alpha[i] += (goal - alpha[i]) * (1 - Math.exp(-dt * rate));
        if (alpha[i] < 1e-4) alpha[i] = 0;
      }
    },
    dispose: () => {
      geometry.dispose();
      material.dispose();
    },
  };
}
